import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      const staffRoles = ["admin", "sales_team", "property_uploader", "accounts"];
      navigate(staffRoles.includes(data.user.role) ? "/admin" : "/portfolio");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portfolio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="overline text-center">Investor Access</div>
        <h1 className="font-serif text-4xl text-center text-[#0B192C] mt-2">Welcome back.</h1>
        <Card className="mt-8">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
              <Button type="submit" className="w-full btn-gold h-11 rounded-full" disabled={loading} data-testid="login-submit">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <div className="text-center my-4 text-xs text-slate-500">or</div>
            <Button variant="outline" className="w-full h-11 rounded-full" onClick={googleLogin} data-testid="login-google">
              Continue with Google
            </Button>
            <div className="flex justify-between text-sm mt-5">
              <Link to="/forgot-password" className="text-slate-600 gold-underline" data-testid="login-forgot">Forgot password?</Link>
              <Link to="/register" className="text-[#0B192C] font-medium gold-underline" data-testid="login-register-link">Create account</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}

export function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [params] = useSearchParams();
  const referral = params.get("ref") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password, referral_code: referral || undefined });
      setUser(data.user);
      toast.success("Welcome to OwnNest!");
      navigate("/kyc");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="overline text-center">Open account</div>
        <h1 className="font-serif text-4xl text-center text-[#0B192C] mt-2">Start owning.</h1>
        {referral && <div className="text-center mt-3 text-sm text-[#0F5132]">Invited with code <span className="font-mono">{referral}</span></div>}
        <Card className="mt-8">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              <div><Label htmlFor="name">Full name</Label><Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="reg-name" /></div>
              <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="reg-email" /></div>
              <div><Label htmlFor="password">Password</Label><Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reg-password" /></div>
              {err && <div className="text-sm text-red-600" data-testid="reg-error">{err}</div>}
              <Button type="submit" className="w-full btn-gold h-11 rounded-full" disabled={loading} data-testid="reg-submit">
                {loading ? "Creating…" : "Create account"}
              </Button>
            </form>
            <div className="text-sm mt-5 text-center">
              Have an account? <Link to="/login" className="text-[#0B192C] font-medium gold-underline">Sign in</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        setUser(data.user);
        window.history.replaceState({}, "", "/portfolio");
        navigate("/portfolio", { replace: true });
      } catch (e) { toast.error("Google sign-in failed"); navigate("/login"); }
    })();
  }, []);
  return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><div className="text-slate-500">Signing you in…</div></div>;
}
